(ns nosco.richtext.schema
  (:require
   [clojure.string :as string]
   [goog.object]
   [nosco.graphql-client]
   [nosco.ui.html :as html]
   ["orderedmap" :as OrderedMap]
   ["prosemirror-schema-list" :refer [addListNodes]]
   ["prosemirror-model" :refer [Schema Fragment]]))

(defn dom-to-attrs
  "Given a dom node and a seq of attrs (string or keyword), return a clojure map
  with those attrs as returned by .getAttribute"
  [node attrs]
  (reduce (fn [acc attr]
            (if-let [v (.getAttribute node (name attr))]
              (assoc acc attr v)
              acc))
          {}
          attrs))

(def ^{:doc "A bunch of iframe attrs that we use all over the place"}
  iframe-attrs [:src
                :srcdoc
                :width
                :height
                :allow
                :allowfullscreen
                :sandbox
                :referrerpolicy])

(defn iframe-info
  "Given an iframe dom element,
  return a map that contains only the html source for the iframe, plus the
  aspect ratio (in percent) of the iframe, falling back to 9/16 "
  [iframe]
  (when iframe
    (let [sanitized-html (.-outerHTML iframe)
          attrs (reduce (fn [acc attr]
                          (assoc acc attr (.getAttribute iframe (name attr))))
                        {}
                        iframe-attrs)
          {:keys [width height]} attrs
          ratio (when (and width height)
                  (* 100.0 (/ (js/parseFloat height) (js/parseFloat width))))
          ratio (or ratio 56.25)]
      (assoc attrs
             :html sanitized-html
             :ratio ratio))))

(defn iframe-node
  "Given an html string that contains (among other things) an iframe,
  return just the iframe dom node, if any"
  [html]
  (let [doc (.parseFromString (js/DOMParser.) html "text/html")
        iframe (.querySelector doc "iframe")]
    iframe))



(def blockquoteDOM #js ["blockquote" 0])
(def hrDOM #js ["hr"])
(def brDOM #js ["br"])

(def underline
  #js {:parseDOM #js [#js {:tag "u"}
                      #js {:style "font-style=underline"}]
       :toDOM (fn []
                #js ["u" 0])})

(def aligned-paragraph
  #js {:content "inline*"
       :group "block"
       :attrs #js {:data-align #js {:default nil}}
       :toDOM (fn [node]
                #js ["p"
                     (.-attrs node)
                     0])
       :parseDOM #js [;; rule that matches only paragraphs that contain <br>
                      #js{:tag "p"
                          :priority 51
                          :getAttrs (fn [dom]
                                      (if (html/node-with-eventually-only-br? dom)
                                        nil ; match with no attributes
                                        false ; skip rule and go to next rule
                                        ))
                          ;; remove the <br> - PM will insert its own <br> into
                          ;; empty paragraphs during editing. We add the <br>
                          ;; back at serialization time.
                          :getContent (fn [] (.-empty Fragment))}

                      #js{:tag "p"
                          :getAttrs (fn [dom]
                                      (let [style (.-style dom)
                                            data-align (.getAttribute dom "data-align")
                                            style-align (.getPropertyValue style "text-align")
                                            attrs-count (.-length style)]
                                        ;; we started out setting direct styles to the paragraph,
                                        ;; but we since needed to also align figures, images etc
                                        ;; so instead we went with data-align and supporting css
                                        ;; but we still need to parse the old dom and also any
                                        ;; redactor-generated stuff
                                        (cond
                                          data-align (clj->js (dom-to-attrs dom [:data-align]))
                                          (and style-align (= attrs-count 1))
                                          #js {:data-align (string/trim style-align)}
                                          :else #js {})))}]})



(def iframe
  #js {:attrs #js {:src #js {:default nil}
                   :srcdoc #js {:default nil}
                   :width #js {:default nil}
                   :height #js {:default nil}
                   :allow #js {:default nil}
                   :allowfullscreen #js {:default nil}
                   :sandbox #js {:default nil}
                   :referrerpolicy #js {:default nil}}
       :disableDropCursor true
       :toDOM (fn [^js node]
                #js ["iframe"
                     (let [attrs (.-attrs node)]
                       attrs)])
       :parseDOM #js [#js{:tag "iframe"
                          :getAttrs (fn [dom]
                                      (clj->js (dom-to-attrs dom iframe-attrs)))}]})

(def figcaption
  #js {:content "text*"
       :toDOM (fn [^js node] #js ["figcaption" (.-attrs node) 0])
       :parseDOM #js [#js {:tag "figcaption"}]})

(def figure
  #js {:content "(image figcaption?) | iframe"
       :group "blocktop"
       :attrs #js {:data-src #js {:default nil}
                   :data-ratio #js {:default nil}
                   :data-align #js {:default nil}
                   :class #js {:default nil}
                   :style #js {:default nil}}
       :atom true
       :draggable true
       :disableDropCursor true
       :toDOM (fn [^js node]
                #js ["figure" (.-attrs node) 0])
       :parseDOM #js [#js {:tag "figure"
                           :getAttrs
                           (fn [^js dom]
                             ;; Remove max-width from redactor-generated HTML
                             (.. dom -style (removeProperty "max-width"))
                             (let [alignment (cond
                                               (.. dom -classList (contains "redactor-alignment-left"))
                                               "left"
                                               (.. dom -classList (contains "redactor-alignment-center"))
                                               "center"
                                               (.. dom -classList (contains "redactor-alignment-right"))
                                               "right"
                                               :else
                                               (.. dom -dataset -align))
                                   _ (.. dom -classList (remove "redactor-alignment-left"
                                                                "redactor-alignment-center"
                                                                "redactor-alignment-right"))
                                   attrs (dom-to-attrs dom [:data-src :class :style :data-align])
                                   attrs (assoc attrs :data-align (or (:data-align attrs) alignment))
                                   ;; if there's an iframe child, start with some base attrs
                                   ;; that will get overriden by attrs directly in the figure node
                                   ;; this is for redactor compatibility
                                   iframe (.querySelector dom "iframe")
                                   iframe-attrs (iframe-info iframe)
                                   base-attrs (when iframe-attrs
                                                {:data-src (:src iframe-attrs)
                                                 :data-ratio (:ratio iframe-attrs)
                                                 :class "nosco-iframe-container"
                                                 :style (str "--figure-ratio:" (:ratio iframe-attrs) "%")})]
                               (clj->js (merge base-attrs attrs))))}]})

(def file-link
  #js {:group "inline"
       :inline true
       :selectable true
       :draggable true
       :isolating false
       :attrs #js {:href #js {:default nil}
                   :file #js {:default nil}
                   :name #js {:default ""}}
       :toDebugString (fn [node]
                        (str "file_link(" (goog.object/getValueByKeys node "attrs" "href") ")"))
       :parseDOM #js [#js{:tag "a[data-file]"
                          :priority 200
                          :getAttrs (fn [dom]
                                      (if-let [file (.getAttribute dom "data-file")]
                                        #js {:href (.getAttribute dom "href")
                                             :file file
                                             :name (.-innerText dom)}
                                        false))}]
       :toDOM (fn [node]
                #js ["a"
                     #js {:data-file (goog.object/getValueByKeys node "attrs" "file")
                          :target "_blank"
                          :download ""
                          ;; data-name is probably redundant and I can't see it being used anywhere;
                          ;; we use data-file to detect when it's a file-link and the link text
                          ;; to parse the filename.
                          ;; :data-name (goog.object/getValueByKeys node "attrs" "name")
                          :href (goog.object/getValueByKeys node "attrs" "href")}
                     (goog.object/getValueByKeys node "attrs" "name")])})

(def mention
  #js {:group "inline"
       :inline true
       :selectable true
       :draggable false
       :isolating false
       :attrs #js {:mention #js {:default nil}
                   :href #js {:default nil}
                   :name #js {:default ""}}
       :toDebugString (fn [node]
                        (str "mention(" (goog.object/getValueByKeys node "attrs" "mention") ")"))
       :parseDOM #js [#js{:tag "a[data-mention]"
                          :priority 200
                          :getAttrs (fn [dom]
                                      (if-let [mention (.getAttribute dom "data-mention")]
                                        #js {:mention mention
                                             :href (.getAttribute dom "href")
                                             :name (.-innerText dom)}
                                        false))}]
       ;; used to generate HTML, so if it matches what parseDOM expects,
       ;; then copy-paste inside the editor works
       :toDOM (fn [node]
                #js ["a"
                     #js {:data-mention (goog.object/getValueByKeys node "attrs" "mention")
                          :href  (goog.object/getValueByKeys node "attrs" "href")}
                     (goog.object/getValueByKeys node "attrs" "name")])})

(def formula-ref
  #js {:group "inline"
       :inline true
       :selectable true
       :draggable false
       :isolating false
       :attrs #js {:formula-ref #js {:default nil}
                   :title #js {:default nil}}
       :toDebugString (fn [node]
                        (str "formula_ref(" (goog.object/getValueByKeys node "attrs" "formula-ref") ")"))
       :parseDOM #js [#js{:tag "span[data-formula-ref]"
                          :priority 200
                          :getAttrs (fn [dom]
                                      (if-let [formula-ref (.getAttribute dom "data-formula-ref")]
                                        #js {:formula-ref formula-ref
                                             :title (.-innerText dom)}
                                        false))}]
       ;; used to generate HTML, so if it matches what parseDOM expects,
       ;; then copy-paste inside the editor works
       :toDOM (fn [node]
                #js ["span"
                     #js {:data-formula-ref (goog.object/getValueByKeys node "attrs" "formula-ref")
                          :title (goog.object/getValueByKeys node "attrs" "title")}
                     (goog.object/getValueByKeys node "attrs" "title")])
       ;; used by textBetween to serialize the node into plain text to extract the formula
       :leafText (fn [node]
                   (str "#" (goog.object/getValueByKeys node "attrs" "formula-ref") " "))})

(def merge-tag
  #js {:group "inline"
       :inline true
       :selectable true
       :draggable false
       :isolating false
       :attrs #js {:merge-tag #js {:default nil}}
       :parseDOM #js [#js{:tag "span[data-merge-tag]"
                          :priority 200
                          :getAttrs (fn [dom]
                                      #js {:merge-tag (.-innerText dom)})}]
       :toDOM (fn [node]
                #js ["span"
                     #js {:data-merge-tag true}
                     (goog.object/getValueByKeys node "attrs" "merge-tag")])})


(def blockquote
  #js {:content "block+"
       :group "blocktop"
       :defining true
       :parseDOM #js [#js {:tag "blockquote"}]
       :toDOM (fn [] blockquoteDOM)})

(def hr #js {:group "blocktop"
             :parseDOM #js [#js {:tag "hr"}]
             :toDOM (fn [] hrDOM)})

(def heading #js {:attrs #js {:level #js {:default 1}}
                  :content "inline*"
                  :group "blocktop"
                  :defining true
                  :parseDOM #js [#js {:tag "h1" :attrs #js {:level 1}}
                                 #js {:tag "h2" :attrs #js {:level 2}}
                                 #js {:tag "h3" :attrs #js {:level 3}}
                                 #js {:tag "h4" :attrs #js {:level 4}}
                                 #js {:tag "h5" :attrs #js {:level 5}}
                                 #js {:tag "h6" :attrs #js {:level 6}}]
                  :toDOM (fn [node] #js [(str "h" (goog.object/getValueByKeys node "attrs" "level"))
                                         0])})

(def image #js {;; note that there's no group here
                ;; so images can only appear within figures
                ;; PM takes care of creating the necessary surrounding nodes as needed

                ;:group "block"
                :inline false
                :attrs #js {:src #js {}
                            :alt #js {:default nil}
                            :title #js {:default nil}
                            :width #js {:default nil}
                            :height #js {:default nil}}
                :disableDropCursor true
                :parseDOM #js [#js {:tag "img[src]"
                                    :getAttrs
                                    (fn [dom]
                                      #js {:src (.getAttribute dom "src")
                                           :title (.getAttribute dom "title")
                                           :alt (.getAttribute dom "alt")
                                           :width (.getAttribute dom "width")
                                           :height (.getAttribute dom "height")})}]
                :toDOM (fn [node]
                         (let [src (goog.object/getValueByKeys node "attrs" "src")
                               alt (goog.object/getValueByKeys node "attrs" "alt")
                               title (goog.object/getValueByKeys node "attrs" "title")
                               width (goog.object/getValueByKeys node "attrs" "width")
                               height (goog.object/getValueByKeys node "attrs" "height")]
                           #js ["img" #js {:src src :alt alt :title title :width width :height height}]))})

(def hard-break #js {:inline true
                     :group "inline"
                     :selectable false
                     :parseDOM #js [#js {:tag "br"}]
                     :toDOM (fn [] brDOM)})

(defn nodes []
  (-> (OrderedMap/from #js {})
      (.addToEnd "doc" #js {:content "(block | blocktop)+"})
      ;; N.B. parapgraph should always come first in order to be the default block node,
      ;; so that Prosemirror will insert an empty paragraph wherever a block node is required.
      (.addToEnd "paragraph" aligned-paragraph)
      (.addToEnd "text" #js {:group "inline"})
      (.addToEnd "blockquote" blockquote)
      (.addToEnd "horizontal_rule" hr)
      (.addToEnd "heading" heading)
      (.addToEnd "image" image)
      (.addToEnd "hard_break" hard-break)
      (.addToEnd "figure" figure)
      (.addToEnd "figcaption" figcaption)
      (.addToEnd "iframe" iframe)
      (.addToEnd "file_link" file-link)
      (.addToEnd "mention" mention)
      (.addToEnd "formula_ref" formula-ref)
      (.addToEnd "merge_tag" merge-tag)
      (addListNodes "paragraph block*" "block")))


(def emDOM #js ["em" 0])
(def strongDOM #js ["strong" 0])

(def marks
  #js {:link #js {:attrs #js {:href #js {}
                              :title #js {:default nil}
                              :target #js {:default nil}}
                  :inclusive false
                  :parseDOM #js [#js {:tag "a[href]"
                                      :getAttrs (fn [dom]
                                                  #js {:href (.getAttribute dom "href")
                                                       :title (.getAttribute dom "title")
                                                       :target (.getAttribute dom "target")})}]
                  :toDOM (fn [node]
                           (let [href (goog.object/getValueByKeys node "attrs" "href")
                                 title (goog.object/getValueByKeys node "attrs" "title")
                                 target (goog.object/getValueByKeys node "attrs" "target")]
                             #js ["a" #js {:href href
                                           :title title
                                           :rel "noopener noreferrer nofollow"
                                           :target target} 0]))}
       :em #js {:parseDOM #js [#js {:tag "i"}
                               #js {:tag "em"}
                               #js {:style "font-style=italic"}]
                :toDOM (fn [] emDOM)}
       :strong #js {:parseDOM #js [#js {:tag "strong"}
                                   #js {:tag "b" :getAttrs
                                        (fn [node]
                                          (and
                                           (not= (goog.object/getValueByKeys node "style" "fontWeight")
                                                 "normal")
                                           nil))}
                                   #js {:style "font-weight"
                                        :getAttrs (fn [value]
                                                    (let [r #"^(bold(er)?|[5-9]\d{2,})$"]
                                                      (and (.test r value) nil)))}]
                    :toDOM (fn [] strongDOM)}
       :underline underline})


(defn nosco-schema []
  (Schema. #js {:nodes (nodes)
                :marks marks}))
